import React, { useState } from "react"
import { Field, Formik, Form } from "formik"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import compose from "ramda/src/compose"
import _isEmpty from "lodash/isEmpty"

import Typograph from "components/Typograph"
import ModernFloatLabelInput from "shared/components/ModernFloatLabelInput"
import FileUploadField from "../FileUploadField"
import HeaderForm from "../HeaderForm"
import FooterForm from "../FooterForm"
import Loading from "components/Loading"
import ModernFloatLabelSelect from "shared/components/ModernFloatLabelSelect"
import { showToast } from "components/Toast"

import styles from "./styles.css"

import { getLocationsBypostCode } from "services/state/actions"
import { maskCep } from "services/utils/mask"
import { stepThreeValidation } from "../validationForm"
import { fileToBase64 } from "services/utils/generateBase64"
import { normalizePayload } from "scenes/SharedEnergy/utils"
import { leadShareEnergy } from "services/state/gedisa/actions"

function AddressDataStep({
  onNextStep,
  onPreviousStep,
  getLocationsBypostCode,
  formData,
  setFormData,
  leadShareEnergy,
  customer,
  handleChangeCloseModal
}) {
  const {
    issueDateOne,
    billOne,
    issueDateTwo,
    billTwo,
    cep,
    address,
    number,
    complement,
    neighborhood,
    city,
    fileEnergy
  } = formData

  const [isLoadingCep, setIsLoadingCep] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [listAddress, setListAddress] = useState([])

  const formatAddressOptions = (address) => {
    return address.map((item) => {
      return {
        label: item.fullName,
        value: item.fullName
      }
    })
  }

  const handleChangeCEP = (e, form, field, setFieldValue) => {
    const masked = maskCep(e.target.value)
    form.setFieldValue(field.name, masked)

    if (masked && masked.length === 9) {
      setIsLoadingCep(true)

      getLocationsBypostCode(masked, (response) => {
        setIsLoadingCep(false)
        if (response && _isEmpty(response.payload.data)) {
          showToast({
            message: "Erro ao buscar informações. Por favor, tente novamente",
            type: "error"
          })
          handleChangeCloseModal()
          return
        }

        const addressData = response.payload.data
        const addressOptions = formatAddressOptions(addressData)

        const selectedAddress = addressData.find(
          (a) => a.fullName === addressOptions[0].value
        )

        const city = selectedAddress.locality
          ? selectedAddress.locality.city
          : ""

        setListAddress(addressData)
        setFieldValue("address", addressOptions[0].value)
        setFieldValue("neighborhood", selectedAddress.neighbourhoods[0].name)
        setFieldValue("city", city)
        setFormData((prevState) => {
          return {
            ...prevState,
            state: selectedAddress.locality.state
          }
        })
      })
    }
  }

  const handleChangeAddress = (e, field, form, setFieldValue) => {
    const selectedAddress = listAddress.find(
      (address) => address.fullName === e.value
    )

    form.setFieldValue(field.name, e.value)
    form.setFieldValue("neighborhood", selectedAddress.neighbourhoods[0].name)
    setFieldValue("city", selectedAddress.locality.city)
    setFormData((prevState) => {
      return {
        ...prevState,
        state: selectedAddress.locality.state
      }
    })
  }

  const addressOption = formatAddressOptions(listAddress)
  const disabledField = _isEmpty(listAddress)

  const showToastError = () => {
    setIsSubmitting(false)
    setFormData({})
    showToast({
      message: "Erro ao enviar a solicitação. Por favor, tente novamente",
      type: "error"
    })
    handleChangeCloseModal()
  }

  return (
    <div>
      <Loading isOpen={isLoadingCep || isSubmitting} />
      <HeaderForm
        title="Abrir solicitação de adesão"
        description="Informações de endereço"
      />

      <div>
        <Formik
          initialValues={{
            issueDateOne,
            billOne,
            issueDateTwo,
            billTwo,
            cep,
            address,
            number,
            complement,
            neighborhood,
            city,
            fileEnergy
          }}
          validationSchema={stepThreeValidation}
          validateOnMount
          onSubmit={async (values) => {
            setIsSubmitting(true)
            try {
              const [fileRG, fileEnergy] = await Promise.all([
                formData.fileRG ? fileToBase64(formData.fileRG) : "",
                values.fileEnergy ? fileToBase64(values.fileEnergy) : ""
              ])

              const payload = {
                ...formData,
                ...values,
                fileRG,
                fileEnergy,
                customer
              }

              const normalized = normalizePayload(payload)

              await new Promise((resolve, reject) => {
                leadShareEnergy(
                  normalized,
                  () => {
                    resolve()
                    setIsSubmitting(false)
                    setFormData({})
                    onNextStep()
                  },
                  (err) => reject(err)
                )
              })
            } catch (error) {
              showToastError()
            }
          }}
        >
          {({ isValid, setFieldValue }) => (
            <Form>
              <div className={styles.form}>
                <Field name="cep">
                  {({ field, meta, form }) => (
                    <ModernFloatLabelInput
                      {...field}
                      id="cep"
                      label="CEP*"
                      error={meta.touched && meta.error}
                      margin="top_3"
                      onChange={(e) =>
                        handleChangeCEP(e, form, field, setFieldValue)
                      }
                    />
                  )}
                </Field>
                <div className={styles.full}>
                  <Field name="address">
                    {({ field, meta, form }) => {
                      const selectedOption =
                        addressOption.find(
                          (opt) => opt.value === field.value
                        ) || null
                      return (
                        <ModernFloatLabelSelect
                          id="address"
                          options={addressOption}
                          value={selectedOption}
                          label="Endereço*"
                          error={meta.touched && meta.error}
                          margin="top_0"
                          isDisabled={disabledField}
                          onChange={(e) =>
                            handleChangeAddress(e, field, form, setFieldValue)
                          }
                        />
                      )
                    }}
                  </Field>
                </div>

                <Field name="number">
                  {({ field, meta }) => (
                    <ModernFloatLabelInput
                      {...field}
                      id="number"
                      label="Número*"
                      error={meta.touched && meta.error}
                      margin="top_0"
                      disabled={disabledField}
                    />
                  )}
                </Field>

                <Field name="complement">
                  {({ field, meta }) => (
                    <ModernFloatLabelInput
                      {...field}
                      id="complement"
                      label="Complemento"
                      error={meta.touched && meta.error}
                      margin="top_0"
                      disabled={disabledField}
                    />
                  )}
                </Field>
                <Field name="neighborhood">
                  {({ field, meta }) => {
                    return (
                      <ModernFloatLabelInput
                        {...field}
                        id="neighborhood"
                        label="Bairro*"
                        error={meta.touched && meta.error}
                        margin="top_0"
                        disabled
                      />
                    )
                  }}
                </Field>
                <Field name="city">
                  {({ field, meta }) => (
                    <ModernFloatLabelInput
                      {...field}
                      id="city"
                      label="Cidade*"
                      error={meta.touched && meta.error}
                      margin="top_0"
                      disabled
                    />
                  )}
                </Field>
              </div>

              <Typograph
                htmlTag="h3"
                size="small"
                weight="semi_bold"
                color="outer_space"
              >
                Conta de energia
              </Typograph>

              <Typograph
                htmlTag="p"
                size="small"
                weight="medium"
                color="outer_space"
              >
                Envie sua fatura de energia para que possamos avaliar o consumo
                e elaborar uma proposta para o seu negócio.
              </Typograph>

              <FileUploadField name="fileEnergy" document="Conta de energia" />

              <FooterForm
                onClickPrevious={onPreviousStep}
                step="3"
                disabled={!isValid}
                textPrevious="Voltar"
                textNext="Enviar dados"
                type="submit"
              />
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}

AddressDataStep.propTypes = {
  onReset: PropTypes.func,
  onNextStep: PropTypes.func,
  setStep: PropTypes.func,
  onPreviousStep: PropTypes.func,
  getLocationsBypostCode: PropTypes.func,
  formData: PropTypes.object,
  setFormData: PropTypes.func,
  leadShareEnergy: PropTypes.func,
  customer: PropTypes.object,
  handleChangeCloseModal: PropTypes.func
}

const enhance = compose(
  connect(null, {
    getLocationsBypostCode,
    leadShareEnergy
  })
)
export default enhance(AddressDataStep)



// WEBPACK FOOTER //
// ./src/scenes/SharedEnergy/components/RequestForm/components/AddressDataStep/index.js